import React from 'react';
import AdSlot from './AdSlotComponent';
import Card from './ui/Card';
import { Link } from 'react-router-dom';

/**
 * Página editorial longa para critérios de qualidade do Google AdSense
 * (conteúdo original, útil e substancial — não apenas ferramenta/navegação).
 */
function GuiaPlanningPoker() {
    return (
        <Card className="p-6 max-w-3xl mx-auto text-left" data-editorial-content="true">
            <h1 className="text-2xl font-bold mb-4">Guia completo: Planning Poker para equipes ágeis</h1>

            <p className="mb-3 text-slate-300">
                Este guia explica o que é o Planning Poker (Scrum Poker), quando usar, como conduzir uma
                sessão produtiva e como ferramentas online como o Pleini Pouquer ajudam times remotos e
                híbridos a estimar backlog com mais clareza e menos viés.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-2">O que é Planning Poker?</h2>
            <p className="mb-3 text-slate-300">
                Planning Poker é uma técnica de estimativa colaborativa usada em Scrum, Kanban e outras
                abordagens ágeis. Cada membro do time recebe um conjunto de cartas com valores (por exemplo,
                sequência de Fibonacci: 1, 2, 3, 5, 8, 13…) e vota em segredo sobre o esforço ou a
                complexidade de uma história de usuário ou tarefa. Depois que todos escolhem uma carta,
                as votações são reveladas ao mesmo tempo. Essa revelação simultânea evita que uma opinião
                forte influencie os demais antes da hora — um problema comum em estimativas abertas por
                voz ou mão levantada.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-2">Por que usar em vez de estimar “de cabeça”?</h2>
            <p className="mb-3 text-slate-300">
                Estimativas informais tendem a ignorar riscos, dependências e o conhecimento distribuído no
                time. O Planning Poker força discussão quando há divergência: se uma pessoa vota 3 e outra
                vota 13, há algo a ser esclarecido — talvez escopo mal entendido, risco técnico não
                visível para todos ou suposições diferentes sobre integração. O objetivo não é acertar
                horas exatas, e sim alinhar percepções e produzir números relativos comparáveis entre
                histórias, úteis para priorização e planejamento de sprint.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-2">Passo a passo de uma sessão típica</h2>
            <ol className="list-decimal list-inside mb-3 text-slate-300 space-y-2">
                <li>
                    O Product Owner ou facilitador apresenta a história com critérios de aceite e responde
                    a dúvidas do time.
                </li>
                <li>
                    Cada desenvolvedor, tester ou designer escolhe uma carta sem revelar até todos estarem
                    prontos.
                </li>
                <li>
                    Revelação simultânea. Se os valores forem próximos, costuma-se fazer uma média ou
                    consenso rápido.
                </li>
                <li>
                    Se houver grande divergência, quem votou nos extremos explica o raciocínio; o grupo
                    discute e vota de novo até convergir ou aceitar um intervalo.
                </li>
                <li>
                    O valor escolhido é registrado no backlog (story points ou unidade que o time adotar).
                </li>
            </ol>

            <h2 className="text-xl font-semibold mt-6 mb-2">Sequências: Fibonacci, linear e outras</h2>
            <p className="mb-3 text-slate-300">
                A sequência de Fibonacci reflete a incerteza crescente: a diferença entre 5 e 8 é menor
                proporcionalmente que entre 13 e 21. Times também usam sequências lineares ou pares quando
                o domínio é muito estável. O importante é manter a mesma escala ao longo do projeto para que
                as comparações entre histórias façam sentido. No Pleini Pouquer você pode escolher o tipo de
                sequência ao criar a sala, adaptando-se ao acordo do seu time.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-2">Planning Poker remoto e híbrido</h2>
            <p className="mb-3 text-slate-300">
                Em times distribuídos, cartas físicas não são práticas. Aplicações web permitem criar salas,
                compartilhar links, cadastrar histórias e revelar votos no mesmo ritmo de uma reunião
                presencial. Isso reduz fricção e documenta a sessão de forma implícita (todos veem o mesmo
                estado da votação). Escolha uma ferramenta simples, com foco na votação e na lista de
                histórias, para não desviar a conversa para configurações complexas.
            </p>

            <h2 className="text-xl font-semibold mt-6 mb-2">Boas práticas e erros comuns</h2>
            <ul className="list-disc list-inside mb-3 text-slate-300 space-y-2">
                <li>
                    <strong className="text-slate-200">Evite estimar histórias grandes demais:</strong>{' '}
                    quebre em partes menores; story points funcionam melhor em itens que cabem em um sprint.
                </li>
                <li>
                    <strong className="text-slate-200">Inclua quem vai executar o trabalho:</strong>{' '}
                    quem não participa da estimativa pode não concordar com o compromisso depois.
                </li>
                <li>
                    <strong className="text-slate-200">Use “?” e pausa com moderação:</strong>{' '}
                    cartas especiais sinalizam necessidade de esclarecimento ou intervalo — abuse delas
                    para não mascarar incerteza com números aleatórios.
                </li>
            </ul>

            <h2 className="text-xl font-semibold mt-6 mb-2">Conclusão</h2>
            <p className="mb-3 text-slate-300">
                O Planning Poker não substitui boa comunicação com o negócio nem refinamento de backlog, mas
                é um dos métodos mais democráticos para transformar estimativas em conversa estruturada. Se
                você busca uma ferramenta gratuita em português para aplicar essas ideias com sua equipe,
                experimente o{' '}
                <Link to="/" className="text-sky-400 hover:underline">
                    Pleini Pouquer
                </Link>
                : crie uma sala, convide o time e comece a votar em minutos. Para dúvidas rápidas, veja
                também a{' '}
                <Link to="/faq" className="text-sky-400 hover:underline">
                    FAQ
                </Link>{' '}
                e a página{' '}
                <Link to="/sobre" className="text-sky-400 hover:underline">
                    Sobre
                </Link>
                .
            </p>

            <p className="text-slate-500 text-sm mt-6">
                Conteúdo editorial atualizado periodicamente. Última revisão do texto: abril de 2026.
            </p>

            <AdSlot adSlot="7348901130475950" format="auto" />
        </Card>
    );
}

export default GuiaPlanningPoker;
